import { ImageOff } from "lucide-react"
import type { RunFeedPreviewResponse } from "@nova-ai/shared"

import { cn } from "../../lib/utils"
import { StatusText } from "../ui/status-text"
import type { FeedRowKey } from "./run-feed-preview-table"
import { NeedsOfferPill } from "./run-offers-needed-badge"
import { SetAsidePill } from "./run-set-aside-pill"
import { buildStorefrontProducts, type StorefrontProduct } from "./run-storefront.derive"
import { VerdictBadge } from "./run-verdict-badge"

type StorefrontLayout = "grid" | "table"

type RunStorefrontProps = {
  preview: RunFeedPreviewResponse
  layout: StorefrontLayout
  selectedRowKey: FeedRowKey | null
  onSelectRow: (row: FeedRowKey) => void
}

export function RunStorefront({
  preview,
  layout,
  selectedRowKey,
  onSelectRow,
}: RunStorefrontProps) {
  const products = buildStorefrontProducts(preview)

  if (products.length === 0) {
    return <StatusText>No products to preview.</StatusText>
  }

  // No header strip: the run header and control band above already carry the
  // run-level counts, so every pixel here goes to the products themselves.
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
      {/* Extra bottom padding keeps the last row scrollable out from under the
          floating load-more pill the run page overlays on this area. */}
      <div className="min-h-0 flex-1 overflow-auto bg-muted/[0.12] p-4 pb-14">
        {layout === "grid" ? (
          <StorefrontGrid
            products={products}
            selectedConfigSku={selectedRowKey?.configSku ?? null}
            onSelect={onSelectRow}
          />
        ) : (
          <StorefrontTable
            products={products}
            selectedConfigSku={selectedRowKey?.configSku ?? null}
            onSelect={onSelectRow}
          />
        )}
      </div>
    </div>
  )
}

function StorefrontGrid({
  products,
  selectedConfigSku,
  onSelect,
}: {
  products: StorefrontProduct[]
  selectedConfigSku: string | null
  onSelect: (row: FeedRowKey) => void
}) {
  const isFeatured = products.length === 1

  return (
    <div
      className={cn(
        "grid gap-4",
        isFeatured
          ? "mx-auto w-full max-w-3xl grid-cols-1"
          : "grid-cols-[repeat(auto-fill,minmax(min(220px,100%),260px))] justify-center sm:justify-start"
      )}
    >
      {products.map((product) => (
        <StorefrontCard
          key={product.configSku}
          product={product}
          selected={selectedConfigSku === product.configSku}
          onSelect={onSelect}
          featured={isFeatured}
        />
      ))}
    </div>
  )
}

function StorefrontCard({
  product,
  selected,
  onSelect,
  featured,
}: {
  product: StorefrontProduct
  selected: boolean
  onSelect: (row: FeedRowKey) => void
  featured: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect({ configSku: product.configSku, simpleSku: product.firstSimpleSku })}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-card transition-colors hover:bg-muted/60",
        featured && "sm:grid sm:grid-cols-[minmax(280px,0.9fr)_minmax(0,1fr)]",
        product.missingTitle && !product.setAside && "border-destructive/50",
        selected && "border-foreground shadow-sm"
      )}
    >
      <div className="absolute left-1.5 top-1.5 z-10">
        {/* A parked config is excluded from the verdict sweep, so its stored
            verdict is whatever it was BEFORE the park — usually "ok" from
            registration, since it passed eligibility. Showing that would claim
            the product ships while the run deliberately drops it, so the park
            REPLACES the badge rather than sitting beside it. */}
        {product.setAside ? (
          <SetAsidePill reason={product.setAsideReason} />
        ) : (
          <VerdictBadge verdict={product.verdict} />
        )}
      </div>

      {product.needsOffer && (
        <div className="absolute right-1.5 top-1.5 z-10">
          <NeedsOfferPill title="No marketplace offer yet for at least one variant" />
        </div>
      )}

      <ProductImage
        url={product.imageUrl}
        alt={product.title}
        className={cn("aspect-square", featured && "sm:aspect-auto sm:h-full sm:min-h-80")}
      />

      <div className={cn("flex flex-1 flex-col gap-2 p-3", featured && "sm:p-6")}>
        <ProductTitle
          title={product.title}
          missing={product.missingTitle}
          setAside={product.setAside}
          featured={featured}
        />

        <CategoryCaption product={product} />

        <div
          className={cn(
            "mt-auto flex items-center gap-2 pt-1 text-xs text-muted-foreground",
            featured && "border-t border-border/70 pt-4"
          )}
        >
          <span className="font-mono tabular-nums">{product.configSku}</span>

          {product.simpleCount !== 1 && (
            <>
              <span className="text-muted-foreground/30">&middot;</span>

              <span>
                {product.simpleCount === 0 ? "No feed rows" : `${product.simpleCount} variants`}
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  )
}

function StorefrontTable({
  products,
  selectedConfigSku,
  onSelect,
}: {
  products: StorefrontProduct[]
  selectedConfigSku: string | null
  onSelect: (row: FeedRowKey) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      {products.map((product) => (
        <button
          key={product.configSku}
          type="button"
          onClick={() =>
            onSelect({ configSku: product.configSku, simpleSku: product.firstSimpleSku })
          }
          className={cn(
            "grid grid-cols-[2.75rem_minmax(0,1fr)] items-start gap-3 rounded-md border border-transparent px-2 py-1.5 text-left transition-colors hover:bg-muted/60 lg:flex lg:items-center",
            product.missingTitle && "border-destructive/40 bg-destructive/5",
            selectedConfigSku === product.configSku && "bg-muted hover:bg-muted"
          )}
        >
          <ProductImage
            url={product.imageUrl}
            alt={product.title}
            className="size-11 shrink-0 rounded-md"
          />

          <div
            data-testid="storefront-table-mobile"
            className="flex min-w-0 flex-col gap-2 lg:hidden"
          >
            <ProductTitle
              title={product.title}
              missing={product.missingTitle}
              setAside={product.setAside}
              testId="storefront-table-mobile-title"
            />

            <div className="flex flex-wrap items-center gap-2">
              {product.needsOffer && (
                <NeedsOfferPill title="No marketplace offer yet for at least one variant" />
              )}

              {product.setAside ? (
                <SetAsidePill reason={product.setAsideReason} />
              ) : (
                <VerdictBadge verdict={product.verdict} />
              )}
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 text-xs text-muted-foreground">
              <span
                data-testid="storefront-table-mobile-sku"
                className="break-all font-mono tabular-nums"
              >
                {product.configSku}
              </span>

              <span className="whitespace-nowrap">{variantCountLabel(product.simpleCount)}</span>
            </div>
          </div>

          <div
            data-testid="storefront-table-desktop"
            className="hidden min-w-0 flex-1 items-center gap-3 lg:flex"
          >
            <div className="min-w-0 flex-1">
              <ProductTitle
                title={product.title}
                missing={product.missingTitle}
                setAside={product.setAside}
                single
                testId="storefront-table-desktop-title"
              />
            </div>

            {product.needsOffer && (
              <NeedsOfferPill title="No marketplace offer yet for at least one variant" />
            )}

            {product.setAside ? (
              <SetAsidePill reason={product.setAsideReason} />
            ) : (
              <VerdictBadge verdict={product.verdict} />
            )}

            <span
              className="hidden w-28 shrink-0 truncate text-xs text-muted-foreground lg:inline"
              title={product.magentoCategoryLeaf ?? undefined}
            >
              {product.marketplaceCategory ?? ""}
            </span>

            <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
              {product.configSku}
            </span>

            <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">
              {variantCountLabel(product.simpleCount)}
            </span>
          </div>
        </button>
      ))}
    </div>
  )
}

// Compact two-category caption: the assigned marketplace category as a chip
// (the listing's real category) plus the Magento source leaf in a muted tone,
// so categories are eyeball-able while scanning the grid.
function CategoryCaption({ product }: { product: StorefrontProduct }) {
  if (!product.marketplaceCategory && !product.magentoCategoryLeaf) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      {product.marketplaceCategory && (
        <span className="truncate rounded bg-muted/60 px-1.5 py-0.5 text-foreground/70">
          {product.marketplaceCategory}
        </span>
      )}

      {product.magentoCategoryLeaf && (
        <span className="truncate text-muted-foreground/70" title="Magento category">
          {product.magentoCategoryLeaf}
        </span>
      )}
    </div>
  )
}

function ProductTitle({
  title,
  missing,
  setAside,
  single,
  featured,
  testId,
}: {
  title: string
  missing: boolean
  setAside?: boolean
  single?: boolean
  featured?: boolean
  testId?: string
}) {
  if (missing) {
    // A parked config has no copy BY DESIGN (it was parked before the copy step,
    // or because its source data is broken), so state the fact without the alarm
    // — the set-aside pill beside it carries the reason. Only an ACTIVE config
    // missing its title is a defect worth shouting about.
    return setAside ? (
      <span className="text-sm text-muted-foreground">— no title (set aside) —</span>
    ) : (
      <span className="text-sm font-medium text-destructive">— title missing —</span>
    )
  }
  return (
    <span
      data-testid={testId}
      className={cn(
        "leading-snug text-foreground",
        featured ? "text-lg font-medium" : "text-sm",
        single ? "block w-full truncate" : "line-clamp-2"
      )}
    >
      {title}
    </span>
  )
}

function ProductImage({ url, alt, className }: { url: string; alt: string; className?: string }) {
  if (!url) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-muted/40 text-muted-foreground/40",
          className
        )}
      >
        <ImageOff className="size-5" />
      </div>
    )
  }
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={cn("bg-white object-contain", className)}
    />
  )
}

function variantCountLabel(count: number): string {
  if (count === 0) {
    return "No feed rows"
  }
  return count === 1 ? "1 variant" : `${count} variants`
}
